// useColorUtils.js;

// ─── Régua de temperatura ──────────────────────────────────────────────────
// As duas funções abaixo são inversas: o que colorTextToBackground aplica ao ir
// do texto ao fundo, colorBackgroundToText desfaz ao voltar.

// Fundo: luminosidade base 92; quente escurece levemente, frio clareia (± até 6).
const BG_LIGHTNESS = 92;
const BG_LIGHTNESS_SPREAD = 6;
// Saturação: quente aumenta, frio reduz (± até ~25 pontos percentuais).
const SATURATION_SPREAD = 25;
// Matiz: desloca no eixo quente↔frio (± até ~12% do caminho até o alvo).
const HUE_PULL = 0.12;
const WARM_HUE = 30; // laranja
const COLD_HUE = 210; // azul

// Texto derivado do fundo: contraste mínimo sobre o próprio fundo. 4,5:1 é o
// mínimo da WCAG (nível AA) para texto comum.
const TEXT_MIN_CONTRAST = 4.5;

// Texto normal desenhado SOBRE o fundo nos trechos com várias palavras: o
// --text-dark do tema claro (tokens.css). O PDF usa preto, que contrasta mais.
const BODY_TEXT = "#202124";

/** t vai de -1 (frio) a +1 (quente), com 0 = neutro (régua em 50). */
function temperatureFactor(temperature) {
  const tClamped = Math.min(100, Math.max(0, temperature));
  return (tClamped - 50) / 50;
}

/** Diferença angular com sinal, pelo caminho curto: [-180, 180). */
function hueDiff(from, to) {
  return ((to - from + 540) % 360) - 180;
}

/** "#rrggbb" → [r, g, b] em 0–1. */
function hexToRgb(hex) {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
}

/** "#rrggbb" → { h: 0–360, s: 0–1, l: 0–1, chromatic }. */
function hexToHsl(hex) {
  const [r, g, b] = hexToRgb(hex);

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0,
    s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = ((((g - b) / delta) % 6) + 6) % 6;
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      case b:
        h = (r - g) / delta + 4;
        break;
    }
    h = Math.round(h * 60);
  }

  return { h, s, l, chromatic: delta !== 0 };
}

/** h: 0–360, s e l: 0–1 → [r, g, b] em 0–1. */
function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r, g, b;
  if (h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  return [r + m, g + m, b + m];
}

function rgbToHex([r, g, b]) {
  const toHex = (v) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Luminância relativa da WCAG, de [r, g, b] em 0–1. */
function relativeLuminance(rgb) {
  const [r, g, b] = rgb.map((v) => {
    const c = Math.round(v * 255) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function useColorUtils() {
  /**
   * Deriva uma cor de fundo (pastel) a partir da cor do texto.
   *
   * Do texto o fundo herda só matiz e saturação; a luminosidade é a da régua.
   *
   * @param {string} hex - Cor do texto no formato "#rrggbb".
   * @param {number} [temperature=50] - Régua 0–100 que modula o fundo:
   *   - 0   → fundo mais claro, menos saturado e levemente "frio" (matiz puxado p/ azul).
   *   - 75  → comportamento neutro (equivalente ao original: lBg=92, sBg=s).
   *   - 100 → fundo mais saturado, levemente mais escuro e "quente" (matiz puxado p/ vermelho/laranja).
   * @returns {string} Cor de fundo no formato "#rrggbb".
   */
  function colorTextToBackground(hex, temperature = 75) {
    const { h, s, chromatic } = hexToHsl(hex);
    const t = temperatureFactor(temperature);

    const sBg = Math.max(0, Math.min(100, s * 100 + t * SATURATION_SPREAD));
    const lBg = Math.max(0, Math.min(100, BG_LIGHTNESS - t * BG_LIGHTNESS_SPREAD));

    // Quente puxa em direção ao laranja; frio em direção ao azul.
    let hBg = h;
    if (chromatic) {
      const target = t >= 0 ? WARM_HUE : COLD_HUE;
      hBg = (h + hueDiff(h, target) * Math.abs(t) * HUE_PULL + 360) % 360;
    }

    return rgbToHex(hslToRgb(hBg, sBg / 100, lBg / 100));
  }

  /**
   * Deriva a cor do texto a partir da cor de fundo - o sentido inverso de
   * {@link colorTextToBackground}.
   *
   * Matiz e saturação: desfaz o deslocamento que a temperatura aplicou, para
   * que escolher um fundo devolva a mesma família de cor do texto que o geraria.
   *
   * Luminosidade: o fundo não a guarda (é fixada pela régua), então ela sai de
   * outro critério - legibilidade SOBRE O FUNDO escolhido. O texto vai para o
   * lado que mais contrasta com ele: escuro sobre fundo claro, claro sobre fundo
   * escuro. Desse lado, é o tom mais próximo do fundo, portanto o mais vivo,
   * que ainda tem contraste de pelo menos 4,5:1. Fundo neutro dá texto cinza.
   *
   * Sobre fundo claro, o texto escuro também fica legível na página branca,
   * que é ainda mais clara que o fundo.
   *
   * @param {string} hex - Cor do fundo no formato "#rrggbb".
   * @param {number} [temperature=75] - A mesma régua de colorTextToBackground.
   * @returns {string} Cor do texto no formato "#rrggbb".
   */
  function colorBackgroundToText(hex, temperature = 75) {
    const { h: hBg, s: sBgN, chromatic } = hexToHsl(hex);
    const t = temperatureFactor(temperature);
    const bgLuminance = relativeLuminance(hexToRgb(hex));

    const s = Math.max(0, Math.min(100, sBgN * 100 - t * SATURATION_SPREAD)) / 100;

    // O caminho de ida anda uma fração k do arco curto até o alvo; a volta
    // estica o que sobrou do arco pelo mesmo fator.
    let h = hBg;
    if (chromatic) {
      const target = t >= 0 ? WARM_HUE : COLD_HUE;
      const k = Math.abs(t) * HUE_PULL;
      h = (((target - hueDiff(hBg, target) / (1 - k)) % 360) + 360) % 360;
    }

    // Lado do texto: o que dá mais contraste - contra o preto ou contra o branco.
    const darkText = (bgLuminance + 0.05) / 0.05 >= 1.05 / (bgLuminance + 0.05);

    // Luminância que o texto não pode passar (escuro) ou precisa alcançar
    // (claro) para ter o contraste mínimo com o fundo.
    const limit = darkText
      ? (bgLuminance + 0.05) / TEXT_MIN_CONTRAST - 0.05
      : TEXT_MIN_CONTRAST * (bgLuminance + 0.05) - 0.05;
    const isLegible = (luminance) => (darkText ? luminance <= limit : luminance >= limit);

    // A luminância cresce com a luminosidade, então a busca binária acha, do
    // lado escolhido, a luminosidade mais próxima do fundo que ainda é legível.
    // Os extremos (preto e branco) sempre passam: o lado escolhido garante
    // pelo menos ~4,58:1 com qualquer fundo.
    let legible = darkText ? 0 : 1,
      illegible = darkText ? 1 : 0;
    for (let i = 0; i < 30; i++) {
      const mid = (legible + illegible) / 2;
      if (isLegible(relativeLuminance(hslToRgb(h, s, mid)))) legible = mid;
      else illegible = mid;
    }

    return rgbToHex(hslToRgb(h, s, legible));
  }

  /**
   * Limita uma cor de fundo escolhida pelo usuário ao que mantém legível o
   * texto normal desenhado sobre ela nos trechos com várias palavras.
   *
   * Fundo que já tem contraste de 4,5:1 com o texto normal volta intacto. Mais
   * escuro que isso, é clareado na mesma família de cor (matiz e saturação
   * preservados) até a luminosidade mínima que alcança o contraste.
   *
   * @param {string} hex - Cor do fundo no formato "#rrggbb".
   * @returns {string} Cor do fundo, limitada, no formato "#rrggbb".
   */
  function legibleBackground(hex) {
    const minLuminance =
      TEXT_MIN_CONTRAST * (relativeLuminance(hexToRgb(BODY_TEXT)) + 0.05) - 0.05;
    if (relativeLuminance(hexToRgb(hex)) >= minLuminance) return hex;

    // Busca binária entre a luminosidade atual (escura demais) e o branco.
    const { h, s, l } = hexToHsl(hex);
    let dark = l,
      legible = 1;
    for (let i = 0; i < 30; i++) {
      const mid = (dark + legible) / 2;
      if (relativeLuminance(hslToRgb(h, s, mid)) >= minLuminance) legible = mid;
      else dark = mid;
    }

    return rgbToHex(hslToRgb(h, s, legible));
  }

  return { colorTextToBackground, colorBackgroundToText, legibleBackground };
}
